"use strict";
/*
 * © 2026 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateTolerances = calculateTolerances;
exports.getToleranceBand = getToleranceBand;
exports.resolveNominal = resolveNominal;
exports.calculateProcessTarget = calculateProcessTarget;
exports.calculatePercentageOfToleranceControlLimit = calculatePercentageOfToleranceControlLimit;
const isAbsoluteLimitType = (limitType) => (limitType !== null && limitType !== void 0 ? limitType : "").toLowerCase() === "absolute";
function calculateTolerances(limitType, nominal, lowerLimit, upperLimit) {
    if (isAbsoluteLimitType(limitType)) {
        return {
            lowerTolerance: lowerLimit,
            upperTolerance: upperLimit,
        };
    }
    else {
        return {
            lowerTolerance: nominal + lowerLimit,
            upperTolerance: nominal + upperLimit,
        };
    }
}
const isDefinedNumber = (value) => typeof value === "number" && !isNaN(value);
function nominalOrZero(nominal) {
    return isDefinedNumber(nominal) ? nominal : 0;
}
/** Classifies a tolerance by which limits are defined. A limit of zero is a defined limit. */
function getToleranceBand(lowerLimit, upperLimit) {
    const hasLower = isDefinedNumber(lowerLimit);
    const hasUpper = isDefinedNumber(upperLimit);
    if (hasLower && hasUpper)
        return "Bilateral";
    if (hasUpper)
        return "UpperOnly";
    if (hasLower)
        return "LowerOnly";
    return "None";
}
/**
 * Nominal to use when calculating tolerances.
 * If nominal is not provided, the midpoint is used only when both limits are absolute;
 * otherwise a missing or invalid nominal defaults to zero.
 */
function resolveNominal(limitType, nominal, lowerLimit, upperLimit) {
    if (isDefinedNumber(nominal))
        return nominal;
    if (isAbsoluteLimitType(limitType) &&
        isDefinedNumber(lowerLimit) &&
        isDefinedNumber(upperLimit)) {
        return (lowerLimit + upperLimit) / 2;
    }
    return nominalOrZero(nominal);
}
/**
 * Process target for feature control limits:
 * the centre of the tolerance band for bilateral tolerances, otherwise the nominal.
 * A missing or invalid nominal defaults to zero when the tolerance is not bilateral.
 */
function calculateProcessTarget(tolerance) {
    if (getToleranceBand(tolerance.lowerLimit, tolerance.upperLimit) === "Bilateral") {
        return (tolerance.upperTolerance + tolerance.lowerTolerance) / 2;
    }
    return nominalOrZero(tolerance.nominal);
}
/**
 * Control limit unit (±) as a percentage of tolerance:
 * - Bilateral: % × (UT − LT) / 2
 * - Upper limit only: % × (UT − Nominal)
 * - Lower limit only: % × (Nominal − LT)
 * - No limits: defaults to the nominal
 * A missing or invalid nominal defaults to zero when the tolerance is not bilateral.
 * The result is negative when the nominal lies outside a single-sided limit, or the upper tolerance is below the lower tolerance.
 */
function calculatePercentageOfToleranceControlLimit(tolerance, controlLimitPercent) {
    const proportion = controlLimitPercent / 100;
    switch (getToleranceBand(tolerance.lowerLimit, tolerance.upperLimit)) {
        case "Bilateral":
            return ((proportion * (tolerance.upperTolerance - tolerance.lowerTolerance)) / 2);
        case "UpperOnly":
            return (proportion *
                (tolerance.upperTolerance - nominalOrZero(tolerance.nominal)));
        case "LowerOnly":
            return (proportion *
                (nominalOrZero(tolerance.nominal) - tolerance.lowerTolerance));
        case "None":
            return nominalOrZero(tolerance.nominal);
    }
}
//# sourceMappingURL=tolerances.js.map